import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Progress,
  message,
  Tag
} from 'antd';
import {
  ReloadOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  RiseOutlined,
  TrophyOutlined,
  ShoppingCartOutlined,
  UserAddOutlined,
  BankOutlined,
  PercentageOutlined,
  LineChartOutlined,
  PieChartOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

// Extend dayjs with isBetween plugin
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const IncomeReport = () => {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  
  const [incomeData, setIncomeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  // Real API calls using fetch
const fetchIncomeData = async () => {
  const token = localStorage.getItem('token');
  const url = `${API_URL}/report/income-reports/summary?from=${dateRange[0].format('YYYY-MM-DD')}&to=${dateRange[1].format('YYYY-MM-DD')}`;

  console.log('📡 Fetching income data from URL:', url);

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!response.ok) throw new Error('Failed to fetch income data');

  const result = await response.json();
  console.log('✅ Income data fetched:', result);

  return result;
};

const loadData = async () => {
  if (!API_URL) {
    setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
    return;
  }

  const token = localStorage.getItem('token');
  if (!token) {
    setError('Authentication token not found. Please log in again.');
    return;
  }

  setLoading(true);
  setError(null);

  try {
    const data = await fetchIncomeData();
    setIncomeData(data);
    message.success('Income data loaded successfully');
  } catch (err) {
    console.error('❌ Error loading income data:', err);

    let errorMessage = 'Failed to load income data';
    if (err.status) {
      if (err.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.status === 400) {
        errorMessage = 'Invalid request parameters';
      } else {
        errorMessage = `Server error: ${err.status}`;
      }
    } else {
      errorMessage = err.message || 'An unexpected error occurred';
    }

    setError(errorMessage);
    message.error(errorMessage);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('income-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Income Analytics Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  // Calculate analytics
  const totalIncome = parseFloat(incomeData?.total_income || 0);
  const registrationIncome = parseFloat(incomeData?.total_registration_income || 0);
  const salesIncome = parseFloat(incomeData?.total_sales_income || 0);
  
  const registrationPercentage = totalIncome > 0 ? (registrationIncome / totalIncome) * 100 : 0;
  const salesPercentage = totalIncome > 0 ? (salesIncome / totalIncome) * 100 : 0;

  // Calculate daily averages
  const daysDiff = dateRange[1].diff(dateRange[0], 'day') || 1;
  const avgDailyIncome = totalIncome / daysDiff;
  const avgDailyRegistration = registrationIncome / daysDiff;
  const avgDailySales = salesIncome / daysDiff;

  // Prepare chart data
  const incomeBreakdownData = [
    {
      name: 'Registration Income',
      amount: registrationIncome,
      percentage: registrationPercentage,
      color: '#52c41a'
    },
    {
      name: 'Sales Income',
      amount: salesIncome,
      percentage: salesPercentage,
      color: '#1890ff'
    }
  ].filter(item => item.amount > 0);

  const comparisonData = [
    {
      category: 'Registration',
      amount: registrationIncome,
      percentage: registrationPercentage
    },
    {
      category: 'Sales',
      amount: salesIncome,
      percentage: salesPercentage
    }
  ];

  // Performance indicators
  const getPerformanceColor = (percentage) => {
    if (percentage >= 70) return '#52c41a';
    if (percentage >= 50) return '#faad14';
    if (percentage >= 30) return '#fa8c16';
    return '#f5222d';
  };

  const getDominantIncomeSource = () => {
    if (registrationIncome > salesIncome) {
      return {
        source: 'Registration',
        amount: registrationIncome,
        percentage: registrationPercentage,
        icon: <UserAddOutlined />,
        color: '#52c41a'
      };
    } else if (salesIncome > registrationIncome) {
      return {
        source: 'Sales',
        amount: salesIncome,
        percentage: salesPercentage,
        icon: <ShoppingCartOutlined />,
        color: '#1890ff'
      };
    } else {
      return {
        source: 'Balanced',
        amount: totalIncome / 2,
        percentage: 50,
        icon: <BankOutlined />,
        color: '#722ed1'
      };
    }
  };

  const dominantSource = getDominantIncomeSource();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${payload[0].name}`}</p>
          <p style={{ color: payload[0].color }}>
            {`Amount: ${payload[0].value.toFixed(2)}`}
          </p>
          <p style={{ color: payload[0].color }}>
            {`Percentage: ${payload[0].payload.percentage.toFixed(1)}%`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="income-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Income Analytics Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Income Analytics Report
          </Title>
          <Text type="secondary">
            Comprehensive revenue analysis from registrations and sales activities
          </Text>
        </div>

        {/* Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Space>
              <CalendarOutlined />
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                Update Report
              </Button>
              <Button 
                type="default" 
                icon={<FilePdfOutlined />}
                onClick={handlePrintPDF}
                disabled={!incomeData}
              >
                Print PDF
              </Button>
            </Space>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Income"
                  value={totalIncome}
                  precision={2}
                  prefix={<BankOutlined />}
                  valueStyle={{ color: '#722ed1', fontSize: '28px' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Registration Income"
                  value={registrationIncome}
                  precision={2}
                  prefix={<UserAddOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary">{registrationPercentage.toFixed(1)}% of total</Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Sales Income"
                  value={salesIncome}
                  precision={2}
                  prefix={<ShoppingCartOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary">{salesPercentage.toFixed(1)}% of total</Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Daily Average"
                  value={avgDailyIncome}
                  precision={2}
                  prefix={<LineChartOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary">Over {daysDiff} days</Text>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Dominant Income Source Highlight */}
          <Card 
            title={
              <Space>
                <TrophyOutlined style={{ color: '#faad14' }} />
                Primary Income Source
              </Space>
            }
            style={{ 
              marginBottom: '24px',
              background: `linear-gradient(135deg, ${dominantSource.color}15 0%, ${dominantSource.color}08 100%)`
            }}
          >
            <Row align="middle" gutter={24}>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: dominantSource.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    color: 'white',
                    fontSize: '32px'
                  }}>
                    {dominantSource.icon}
                  </div>
                  <Title level={3} style={{ margin: '8px 0', color: dominantSource.color }}>
                    {dominantSource.source}
                  </Title>
                  <Text style={{ fontSize: '18px' }}>
                    <strong>{dominantSource.amount.toFixed(2)}</strong> ({dominantSource.percentage.toFixed(1)}%)
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={16}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <Text>Registration Income</Text>
                      <Text strong>{registrationIncome.toFixed(2)}</Text>
                    </div>
                    <Progress 
                      percent={registrationPercentage}
                      strokeColor="#52c41a"
                      format={(percent) => `${percent.toFixed(1)}%`}
                    />
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <Text>Sales Income</Text>
                      <Text strong>{salesIncome.toFixed(2)}</Text>
                    </div>
                    <Progress 
                      percent={salesPercentage}
                      strokeColor="#1890ff"
                      format={(percent) => `${percent.toFixed(1)}%`}
                    />
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Income Distribution Pie Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <PieChartOutlined style={{ color: '#722ed1' }} />
                    Income Distribution
                  </Space>
                }
              >
                {incomeBreakdownData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={incomeBreakdownData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${name} (${percentage.toFixed(1)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="amount"
                        >
                          {incomeBreakdownData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="large">
                        {incomeBreakdownData.map((entry) => (
                          <Space key={entry.name}>
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: entry.color,
                                borderRadius: '50%' 
                              }}
                            />
                            <Text>{entry.name}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No income data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Income Comparison Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <RiseOutlined style={{ color: '#52c41a' }} />
                    Income Comparison
                  </Space>
                }
              >
                {comparisonData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={comparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" />
                      <YAxis />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="amount" 
                        fill="#8884d8"
                        radius={[4, 4, 0, 0]}
                      >
                        {comparisonData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#52c41a' : '#1890ff'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No comparison data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Daily Averages Breakdown */}
          <Card 
            title={
              <Space>
                <PercentageOutlined style={{ color: '#fa8c16' }} />
                Daily Average Breakdown
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Daily Total"
                    value={avgDailyIncome}
                    precision={2}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                  <Text type="secondary">Average per day</Text>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Daily Registration"
                    value={avgDailyRegistration}
                    precision={2}
                    valueStyle={{ color: '#52c41a' }}
                  />
                  <Text type="secondary">Registration income/day</Text>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Daily Sales"
                    value={avgDailySales}
                    precision={2}
                    valueStyle={{ color: '#1890ff' }}
                  />
                  <Text type="secondary">Sales income/day</Text>
                </Card>
              </Col>
            </Row>
          </Card>

          {/* Performance Insights */}
          {incomeData && (
            <Card title="Revenue Insights" style={{ marginTop: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Tag color={getPerformanceColor(registrationPercentage)} style={{ fontSize: '16px', padding: '8px 16px' }}>
                      {registrationPercentage >= 60 ? 'Strong' : registrationPercentage >= 40 ? 'Moderate' : 'Developing'} Registration Base
                    </Tag>
                    <div style={{ marginTop: '12px' }}>
                      <Text type="secondary">
                        Registration income represents {registrationPercentage.toFixed(1)}% of total revenue
                      </Text>
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Tag color={getPerformanceColor(salesPercentage)} style={{ fontSize: '16px', padding: '8px 16px' }}>
                      {salesPercentage >= 60 ? 'Strong' : salesPercentage >= 40 ? 'Moderate' : 'Developing'} Sales Performance
                    </Tag>
                    <div style={{ marginTop: '12px' }}>
                      <Text type="secondary">
                        Sales income represents {salesPercentage.toFixed(1)}% of total revenue
                      </Text>
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Tag color="#722ed1" style={{ fontSize: '16px', padding: '8px 16px' }}>
                      {Math.abs(registrationPercentage - salesPercentage) <= 20 ? 'Balanced' : 'Focused'} Revenue Mix
                    </Tag>
                    <div style={{ marginTop: '12px' }}>
                      <Text type="secondary">
                        Revenue distribution shows {Math.abs(registrationPercentage - salesPercentage) <= 20 ? 'balanced' : 'specialized'} approach
                      </Text>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          )}
        </Spin>

        <style jsx>{`
          .print-header, .print-date-range {
            display: none;
          }
          
          @media print {
            .print-header, .print-date-range {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default withRoleAccess(IncomeReport, ['superadmin']);